import { memo, useRef, useState } from 'react';
import { cn } from '../../cn';
import { Spinner } from '../../ui/spinner';
import { getProjectCoverThumbUrl } from '../data/coverThumb';

/*
 * Превью, которые в этой вкладке уже загрузились или не нашлись. Строки списка создаются заново при смене таба и раскрытии
 * месяца: без памяти каждая обложка снова начинала с пустой плитки и проявлялась, хотя картинка в кеше, — список моргал.
 */
const loadedCovers = new Set<string>();
const failedCovers = new Set<string>();

/**
 * Обложка 1:1 — превью с нашего сервера (webp, data/coverThumb), не полная обложка с CDN Эксмо: полную грузит только
 * просмотрщик. Вытянутые обложки обрезаются по верхнему краю — там обычно название. Нет ASTA, обложки на CDN нет
 * (сервер ответил 404) или картинка не загрузилась — плитка с первой буквой названия. Картинка видна, когда загрузилась,
 * до того — плитка с лоадером.
 * С onOpen показанная обложка — кнопка «открыть» (просмотрщик, ui/image-viewer); плитка с буквой не нажимается.
 */
export const ProjectCover = memo(function ProjectCover({
  asta,
  name,
  className,
  onOpen,
  onOpenIntent,
}: {
  asta: string | null | undefined;
  name: string;
  className?: string;
  /** Нажали на показанную обложку; img — превью, из которого вырастает просмотр */
  onOpen?: (img: HTMLImageElement) => void;
  /** Указатель или фокус на обложке-кнопке — её, скорее всего, сейчас откроют: можно заранее грузить полную */
  onOpenIntent?: () => void;
}) {
  const url = getProjectCoverThumbUrl(asta);
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const [loadedUrl, setLoadedUrl] = useState<string | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const base = 'nd:aspect-square nd:shrink-0 nd:overflow-hidden nd:rounded-[0.3rem]';
  /*
   * Обводка и тень. У нажимаемой обложки — на кнопке, а не на картинке: просмотрщик прячет картинку-превью и растит из неё
   * свою копию без обводки — снималась бы и обводка, край обложки моргал. На кнопке она остаётся, пока копия улетает.
   */
  const frame = 'nd:shadow-sm nd:ring-1 nd:ring-black/15';

  if (!url || failedUrl === url || failedCovers.has(url)) {
    const letter = name.trim().charAt(0).toUpperCase() || '?';
    return (
      <div
        aria-hidden="true"
        className={cn(
          base,
          frame,
          'nd:grid nd:place-items-center nd:bg-muted nd:font-semibold nd:text-foreground/45 nd:shadow-none nd:ring-foreground/5 nd:[container-type:inline-size]',
          className
        )}
      >
        <span className="nd:text-[40cqw] nd:leading-none">{letter}</span>
      </div>
    );
  }

  // Уже загружалась — сразу видна: без подложки с лоадером и без проявления
  const shown = loadedUrl === url || loadedCovers.has(url);
  // Пока не загрузилась — плитка как у буквы, с лоадером; картинка прозрачна, без недорисованной
  const pending = 'nd:ring-1 nd:ring-foreground/5';
  const content = (
    <>
      <img
        ref={imgRef}
        src={url}
        alt=""
        loading="lazy"
        decoding="async"
        onError={() => {
          failedCovers.add(url);
          setFailedUrl(url);
        }}
        onLoad={() => {
          loadedCovers.add(url);
          setLoadedUrl(url);
        }}
        className={cn(
          'nd:block nd:size-full nd:rounded-[inherit] nd:object-cover nd:object-top',
          // Загрузилась — проявляется поверх серой подложки, а не возникает рывком
          'nd:transition-opacity nd:duration-300 nd:ease-(--ease-out-ui) nd:motion-reduce:transition-none',
          !shown && 'nd:opacity-0'
        )}
      />
      {shown ? null : (
        // Лоадер проявляется с задержкой: обложка из кеша загружается сразу, и он бы только мигнул
        <span
          aria-hidden="true"
          className="nd:absolute nd:inset-0 nd:grid nd:place-items-center nd:text-foreground/45 nd:animate-[nd-fade-in_200ms_var(--ease-out-ui)_150ms_both]"
        >
          <Spinner className="nd:text-[clamp(0.75rem,35cqw,1.5rem)]" />
        </span>
      )}
    </>
  );
  // Подложка остаётся и под загруженной: пока картинка проявляется, сквозь неё не просвечивает пустое место
  const box = 'nd:relative nd:bg-muted nd:[container-type:inline-size]';

  if (!onOpen) {
    return (
      <div className={cn(base, box, 'nd:transition-shadow nd:duration-300 nd:ease-(--ease-out-ui)', shown ? frame : pending, className)}>
        {content}
      </div>
    );
  }

  // Кнопка — и пока обложка грузится (выключена): иначе <img> пересоздался бы при появлении кнопки и загрузился заново
  return (
    <button
      type="button"
      aria-label="Открыть обложку"
      disabled={!shown}
      onClick={() => imgRef.current && onOpen(imgRef.current)}
      onPointerEnter={onOpenIntent}
      onFocus={onOpenIntent}
      className={cn(
        box,
        // Без сжатия при нажатии: обложка сразу вырастает в просмотр, сжатие перед этим читалось бы рывком
        'nd:block nd:aspect-square nd:shrink-0 nd:cursor-pointer nd:rounded-[0.3rem] nd:focus-ring nd:disabled:cursor-default',
        'nd:transition-[filter,box-shadow] nd:duration-(--press-duration) nd:ease-(--ease-out-ui) nd:enabled:hover:brightness-[0.94]',
        shown ? frame : pending,
        className
      )}
    >
      {content}
    </button>
  );
});
